import type { ComboboxOptionProps } from '@headlessui/react';
import type { ReactNode } from 'react';

import { ComboboxOption } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import { CheckmarkIcon } from '../../../images/shapes';

export interface DropdownOption extends Record<string, any> {
  label: ReactNode;
  value: any | null;
  searchString?: string;
  optionLabel?: ReactNode;
  group?: string;
  disabled?: boolean;
  ignoreCheckmark?: boolean;
  disabledAdditionClass?: string;
  circleColor?: string;
  notFound?: boolean;
}

interface DropdownOptionWrapperProps extends Omit<
  ComboboxOptionProps,
  'value'
> {
  option: DropdownOption;
  groupName?: string;
  additionalOptionsClasses?: string;
  isVirtualScrolling?: boolean;
  testId?: string;
}

const DropdownOptionWrapper = ({
  option,
  groupName,
  additionalOptionsClasses,
  isVirtualScrolling,
  testId,
  ...props // headless ui passed props to combobox option
}: DropdownOptionWrapperProps) => {
  return (
    <ComboboxOption
      as="li"
      className={twMerge(
        'w-full',
        'hover:bg-blue-300 dark:hover:bg-transparent',
        // When virtual scrolling there is some issue that every option has data-focus attribute
        !isVirtualScrolling &&
          'focus:bg-blue-300 dark:data-focus:bg-transparent',
        'cursor-default select-none py-2 pl-4',
        groupName && 'pl-6',
        option.disabled
          ? 'bg-slate-100 cursor-not-allowed'
          : 'hover:cursor-pointer dark:hover:bg-slate-700',
        additionalOptionsClasses,
        option.disabled && option.disabledAdditionClass,
      )}
      disabled={option.disabled}
      {...getTestProps(testId, `${option.value}-option`)}
      {...props}
      value={option.value}
    >
      {({ selected }) => (
        <div className="flex flex-row items-center justify-between">
          <span className="flex items-center min-h-[21px] dark:text-white w-full">
            <span
              className={twMerge(
                'h-2 w-2 mr-2 rounded-full',
                option.circleColor || 'hidden',
              )}
            />
            {option.optionLabel || option.label || option.value}
          </span>
          <span className={twMerge('text-blue pr-1 md:pr-5')}>
            {selected && !option.ignoreCheckmark && (
              <CheckmarkIcon
                className="w-3.5 text-blue"
                {...getTestProps(testId, `${option.value}-selected`)}
              />
            )}
          </span>
        </div>
      )}
    </ComboboxOption>
  );
};

export default DropdownOptionWrapper;
